"use client"

import { motion } from "framer-motion";
import Image from "next/image";
import { Badge } from "@/components/ui/badge";
import {
    BookOpen,
    Braces,
    Brackets,
    Database,
    Globe2,
    Workflow,
} from "lucide-react";
import { Products } from "@/types/productsTypes";

const languageIconMap = {
    language: BookOpen,
    code: Braces,
    structured: Database,
    workflow: Workflow,
    globe: Globe2,
    markup: Brackets,
} as const;

export default function LanguageSupport({ section }: { section: Products['atlaside']['languageSupport'] }) {
    return (
        <section className="section-bg-darker py-32 -mx-12 px-12">
            <div className="max-w-6xl mx-auto relative z-10">
                <div className="text-center mb-16">
                    <motion.span
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="inline-block w-fit mx-auto badge-size section-badge mb-1"
                    >
                        {section.badge}
                    </motion.span>

                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="title-size font-bold mb-6 bg-gradient-to-r from-gray-900 via-blue-900 to-indigo-900 bg-clip-text text-transparent"
                    >
                        {section.title}
                    </motion.h2>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="text-size text-gray-600 max-w-3xl mx-auto leading-relaxed"
                    >
                        {section.description}
                    </motion.p>
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-14">
                    {section.cards.map((lang, index) => {
                        const Icon = lang.icon ? languageIconMap[lang.icon as keyof typeof languageIconMap] : null;

                        return (
                            <motion.div
                                key={lang.name}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: index * 0.1 }}
                                className="group flex flex-col items-center text-center"
                            >
                                <div className="size-12 md:size-16 mb-4 flex items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700">
                                    {Icon ? (
                                        <Icon className="size-6" />
                                    ) : (
                                        lang.logo && (
                                            <Image
                                                src={lang.logo}
                                                alt={lang.name}
                                                width={100}
                                                height={100}
                                                className="object-contain"
                                            />
                                        )
                                    )}
                                </div>

                                <div className="space-y-3">
                                    <h3 className="text-xl md:text-2xl font-bold text-gray-600">{lang.name}</h3>
                                    <Badge
                                        variant={lang.status === "active" ? "gradient" : "gray"}
                                        className="text-[11px] md:text-xs text-white"
                                    >
                                        {lang.status === "active" ? "Supported" : "Coming Soon"}
                                    </Badge>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>

                {section.comingSoonText && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.4 }}
                        className="text-center mt-12"
                    >
                        <div className="inline-flex items-center gap-2 px-5 py-3 bg-white/80 backdrop-blur-sm rounded-full border border-gray-200 text-sm font-medium text-gray-700">
                            <div className="w-2 h-2 rounded-full bg-purple-500 animate-pulse" />
                            {section.comingSoonText}
                        </div>
                    </motion.div>
                )}
            </div>
        </section>
    );
}
